import { useState, useEffect, useRef } from 'react';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column, ColumnEditorOptions } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Panel } from 'primereact/panel';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Controller, useForm } from 'react-hook-form';
import { classNames } from 'primereact/utils';
import { Dialog } from 'primereact/dialog';
import Except from '../../models/Except';
import { ExceptService } from '../../service/ExceptService';
import { msalInstance } from '../../service/msal';
import { Link, useParams } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import SetupOption from '../../models/SetupOpt';
import { Dropdown } from 'primereact/dropdown';
import Style from '../../models/Style';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

const StyleExcept = () => {
    const emptyExcept: Except = {
        exceptId: 0,
        exceptStyleCd: '',
        exceptSetupOptId: 0,
        exceptColorOpt: 0,
        exceptSzOpt: 0,
        exceptLtrItmClass: '',
        exceptLtrStyle: '',
        exceptTimStp: new Date().toISOString(),
        exceptUpdUser: '',
        exceptUpdTs: new Date().toISOString(),
        exceptColorOptDesc: '',
        exceptSetupOptDesc: '',
        exceptSzOptDesc: '',
        exceptLtrStyleDesc: ''
    };

    const [excepts, setExcepts] = useState<Except[]>([]);
    const [setupOpt, setSetupOpt] = useState<SetupOption[]>([]);
    const [setupOptEdit, setSetupOptEdit] = useState<SetupOption[]>([]);
    const [setupOptColor, setSetupOptColor] = useState<SetupOption[]>([]);
    const [setupOptColorEdit, setSetupOptColorEdit] = useState<SetupOption[]>([]);
    const [setupOptSize, setSetupOptSize] = useState<SetupOption[]>([]);
    const [setupOptSizeEdit, setSetupOptSizeEdit] = useState<SetupOption[]>([]);
    const [letteringStyles, setLetteringStyles] = useState<Style[]>([]);
    const [loadingExcepts, setLoadingExcepts] = useState(true);
    const [except, setExcept] = useState<Except>(emptyExcept);
    const [deleteExceptDialog, setDeleteExceptDialog] = useState(false);
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [filters, setFilters] = useState<DataTableFilterMeta>({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [editingRows, setEditingRows] = useState({});
    const [isRowEditingExcept, setIsRowEditingExcept] = useState(false);
    const isNew = useRef(false);
    const toast = useRef<any>(null);
    const account = msalInstance.getActiveAccount();
    const { styleCode } = useParams();

    useEffect(() => {
        if (!styleCode) { return; }

        ExceptService.getExceptsByStyle(styleCode).then(data => {
            setExcepts(data);
        });

        ExceptService.getSetupOptByStyleCd(styleCode).then(data => {
            setSetupOpt(data);
        });

        ExceptService.getSetupOptSizeByStyleCd(styleCode).then(data => {
            setSetupOptSize(data);
        });

        ExceptService.getLetteringStyles('').then(data => {
            setLetteringStyles(data);
        });

        setLoadingExcepts(false);
    }, [styleCode]);

    const reloadData = () => {
        if (!styleCode) { return; }

        ExceptService.getExceptsByStyle(styleCode).then(data => {
            setExcepts(data);
        });
    }

    const {
        control: controlExcept,
        setError: exceptSetError,
        formState: { errors: errorsExcept },
        reset: resetExcept,
        handleSubmit: handleSubmitExcept
    } = useForm();

    const {
        control: formEditExcept,
        setError: exceptSetErrorEdit,
        reset: resetExceptEdit,
    } = useForm();

    const onSubmitExcept = (data: any) => {
        if (!styleCode) { return; }

        isNew.current = true;
        if (data.exceptSetupOptId == null && data.exceptColorOpt == null && data.exceptSzOpt == null && data.exceptLtrStyle == null) {
            exceptSetError("exceptSetupOptId", { type: "error" });
            exceptSetError("exceptColorOpt", { type: "error" });
            exceptSetError("exceptSzOpt", { type: "error" });
            exceptSetError("exceptLtrStyle", { type: "error" });
            toast.current.show({ severity: 'error', summary: 'Failed', detail: 'At least one exception is required.', life: 3000 });
            return;
        }
        if (excepts) {
            let checkDuplicate = false;
            excepts.forEach(except => {
                if (except.exceptSetupOptId === (data.exceptSetupOptId ?? 0)
                    && except.exceptColorOpt === (data.exceptColorOpt ?? 0)
                    && except.exceptSzOpt === (data.exceptSzOpt ?? 0)
                    && except.exceptLtrStyle === data.exceptLtrStyle) {
                    checkDuplicate = true;
                    return;
                }
            });

            if (checkDuplicate) {
                exceptSetError("exceptSetupOptId", { type: "error" });
                exceptSetError("exceptColorOpt", { type: "error" });
                exceptSetError("exceptSzOpt", { type: "error" });
                exceptSetError("exceptLtrStyle", { type: "error" });
                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Duplicate entry detected.', life: 3000 });
                return;
            }
        }

        let _except = { ...except };
        _except.exceptStyleCd = styleCode;
        _except.exceptSetupOptId = data.exceptSetupOptId ?? 0;
        _except.exceptSetupOptDesc = data.exceptSetupOptDesc ?? '';
        _except.exceptColorOpt = data.exceptColorOpt ?? 0;
        _except.exceptColorOptDesc = data.exceptColorOptDesc ?? '';
        _except.exceptSzOpt = data.exceptSzOpt ?? 0;
        _except.exceptSzOptDesc = data.exceptSzOptDesc ?? '';
        _except.exceptLtrItmClass = data.exceptLtrItmClass;
        _except.exceptLtrStyle = data.exceptLtrStyle;
        _except.exceptTimStp = new Date().toISOString();
        _except.exceptUpdUser = account !== null ? account.username.split('@')[0] : '';
        _except.exceptUpdTs = new Date().toISOString();

        saveExcept(_except);
    };

    const getExceptFormErrorMessage = (name: string) => {
        return errorsExcept[name] ? <small className="p-error">{(errorsExcept[name] as any).message}</small> : <small className="p-error">&nbsp;</small>;
    };

    const hideDeleteExceptDialog = () => {
        setDeleteExceptDialog(false);
    }

    const saveExcept = (exceptData: Except) => {
        if (!styleCode) { return; }

        let _excepts = [...excepts];

        if (isNew.current) {
            ExceptService.addExcept(exceptData).then((data) => {
                if (data) {
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Except Created', life: 3000 });

                    ExceptService.getExceptsByStyle(styleCode).then(exceptData => {
                        setExcepts(exceptData);
                        setLoadingExcepts(false);
                    });

                    setExcept(emptyExcept);
                    reloadData();
                    setSetupOptColor([]);
                    resetExcept();
                    resetExceptEdit();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Create Except', life: 3000 });
                }
            });
        } else {
            ExceptService.updateExcept(exceptData).then((data) => {
                if (data) {
                    let index = excepts.findIndex(r => r.exceptId === exceptData.exceptId);
                    _excepts[index] = exceptData;
                    toast.current.show({ severity: 'success', summary: "Successful", detail: 'Except Updated', life: 3000 });
                    reloadData();
                    setSetupOptColor([]);
                    setExcept(emptyExcept);
                    resetExcept();
                    resetExceptEdit();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Update Except', life: 3000 });
                }
            });
        }
    }

    const confirmDeleteExcept = (except: Except) => {
        setExcept(except);
        setDeleteExceptDialog(true);
    }

    const deleteExcept = () => {
        ExceptService.deleteExcept(except.exceptId).then((data) => {
            if (data) {
                reloadData();
                setDeleteExceptDialog(false);
                setExcept(emptyExcept);
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Except Deleted', life: 3000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Delete Except' })
            }
        });
    }

    const onRowEditChange = (e: any) => {
        setEditingRows(e.data);
    }

    const onRowClickExcept = (e: any) => {
        if (!styleCode) { return; }
        if (!isRowEditingExcept) {
            let _except = (e.data as Except);
            except.exceptSetupOptId = _except.exceptSetupOptId;
            except.exceptColorOpt = _except.exceptColorOpt;
            except.exceptSzOpt = _except.exceptSzOpt;
            except.exceptLtrStyle = _except.exceptLtrStyle;
            loadDropdownData(_except.exceptLtrStyle ?? '');
            let selectedExceptIndex = excepts.findIndex(v => v.exceptId === _except.exceptId);
            let _editingRows = { ...editingRows, ...{ [`${excepts[selectedExceptIndex].exceptId}`]: true } };
            if (Object.keys(_editingRows).length === 1) {
                setEditingRows(_editingRows);
                setIsRowEditingExcept(true);

                ExceptService.getSetupOptByStyleCd(styleCode).then(data => {
                    setSetupOptEdit(data);
                    ExceptService.getSetupOptByStyleCdAndDesc(styleCode, _except.exceptSetupOptDesc).then(data => {
                        setSetupOptColorEdit(data);
                        ExceptService.getSetupOptSizeByStyleCd(styleCode).then(data => {
                            setSetupOptSizeEdit(data);
                        });
                    });
                });
            }
        }
    };

    const cellEditor = (options: ColumnEditorOptions) => {
        switch (options.field) {
            case 'exceptSetupOptId':
                return exceptSetupOptId_Editor(options);
            case 'exceptColorOpt':
                return exceptColorOpt_Editor(options);
            case 'exceptSzOpt':
                return exceptSzOpt_Editor(options);
            case 'exceptLtrStyle':
                return exceptLtrStyle_Editor(options);
        }
    }

    const exceptSetupOptId_Editor = (options: any) => {
        return (
            <Controller
                name="exceptSetupOptId_Edit"
                control={formEditExcept}
                defaultValue={options.value}
                render={({ field, fieldState }) => {
                    except.exceptSetupOptId = field.value;
                    return (
                        <>
                            <Dropdown value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} options={setupOptEdit} optionLabel="setupOptDesc" optionValue="setupOptId" className={classNames({ 'p-invalid border-left-10': fieldState.error })}
                                onChange={(e) => {
                                    field.onChange(e.value);
                                    if (!styleCode || !setupOpt) { return; }
                                    var setupOptDesc = setupOptEdit.find(option => option.setupOptId === e.value)?.setupOptDesc;
                                    if (!setupOptDesc) { return; }
                                    ExceptService.getSetupOptByStyleCdAndDesc(styleCode, setupOptDesc).then(data => {
                                        setSetupOptColorEdit(data);
                                    });
                                }} placeholder="Detailing Option" />
                        </>
                    )
                }
                }
            />
        );
    }

    const exceptColorOpt_Editor = (options: any) => {
        return (
            <Controller
                name="exceptColorOpt_Edit"
                control={formEditExcept}
                defaultValue={options.value}
                render={({ field, fieldState }) => {
                    except.exceptColorOpt = field.value;

                    return (
                        <>
                            <Dropdown value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} options={setupOptColorEdit} optionLabel="setupOptDesc" optionValue="setupOptId" className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => { field.onChange(e.value); }} placeholder="Color Exception" />
                        </>
                    )
                }
                }
            />
        );
    }

    const exceptSzOpt_Editor = (options: any) => {
        return (
            <Controller
                name="exceptSzOpt_Edit"
                control={formEditExcept}
                defaultValue={options.value}
                render={({ field, fieldState }) => {
                    except.exceptSzOpt = field.value;

                    return (
                        <>
                            <Dropdown value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} options={setupOptSizeEdit} optionLabel="setupOptDesc" optionValue="setupOptId" className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => { field.onChange(e.value); }} placeholder="Size Exception" />
                        </>
                    )
                }
                }
            />
        );
    }

    const exceptLtrStyle_Editor = (options: any) => {
        return (
            <Controller
                name="exceptLtrStyle_Edit"
                control={formEditExcept}
                defaultValue={options.value}
                render={({ field, fieldState }) => {
                    except.exceptLtrStyle = field.value;

                    return (
                        <>
                            <Dropdown
                                value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }}
                                options={letteringStyles}
                                onChange={(e) => field.onChange(e.value)}
                                filter
                                filterBy="styleCode"
                                onFilter={handleDropdownFilter}
                                itemTemplate={styleTemplate}
                                valueTemplate={selectedStyleTemplate}
                                optionLabel="description"
                                optionValue="styleCode"
                                showClear
                                className={classNames({ 'p-invalid border-left-10': fieldState.error })}
                                placeholder="Lettering Style"
                                loading={loading}
                            />
                        </>
                    )
                }
                }
            />
        );
    }

    const exceptId_Template = (rowData: Except) => {
        return rowData.exceptId;
    }

    const exceptSetupOptId_Template = (rowData: Except) => {
        return rowData.exceptSetupOptDesc
    }

    const exceptColorOpt_Template = (rowData: Except) => {
        return rowData.exceptColorOptDesc;
    }

    const exceptSzOpt_Template = (rowData: Except) => {
        return rowData.exceptSzOptDesc;
    }

    const exceptLtrItmClass_Template = (rowData: Except) => {
        return rowData.exceptLtrItmClass;
    }

    const exceptLtrStyle_Template = (rowData: Except) => {
        return rowData.exceptLtrStyleDesc;
    }

    const exceptUpdTs_Template = (rowData: Except) => {
        return formatDate(rowData.exceptUpdTs);
    }

    const exceptUpdUser_Template = (rowData: Except) => {
        return rowData.exceptUpdUser;
    }

    const formatDate = (dateTimeString: string) => {
        const date = new Date(dateTimeString);
        return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    }



    const rowEditorExceptTemplate = (rowData: Except, props: any) => {
        if (!styleCode) { return; }

        const rowEditor = props.rowEditor;


        if (rowEditor.editing) {
            isNew.current = false;

            return (
                <>
                    <Button icon="p-row-editor-save-icon pi pi-fw pi-save" title="Save" rounded text severity="info" type="submit" onClick={(e) => {
                        let _except = { ...except };

                        _except.exceptId = rowData.exceptId;
                        _except.exceptUpdUser = account !== null ? account.username.split('@')[0] : '';
                        _except.exceptUpdTs = new Date().toISOString();
                        _except.exceptStyleCd = styleCode;
                        _except.exceptColorOptDesc = "";
                        _except.exceptSzOptDesc = "";
                        _except.exceptSetupOptDesc = "";
                        _except.exceptLtrStyleDesc = "";

                        let isDuplicate = excepts.some(except =>
                            except.exceptId !== _except.exceptId
                            && Number(except.exceptSetupOptId) === Number(_except.exceptSetupOptId ?? 0)
                            && Number(except.exceptColorOpt) === Number(_except.exceptColorOpt ?? 0)
                            && Number(except.exceptSzOpt) === Number(_except.exceptSzOpt ?? 0)
                            && String(except.exceptLtrStyle) === String(_except.exceptLtrStyle)
                        );

                        if (isDuplicate) {
                            exceptSetErrorEdit("exceptSetupOptId_Edit", { type: "error" });
                            exceptSetErrorEdit("exceptColorOpt_Edit", { type: "error" });
                            exceptSetErrorEdit("exceptSzOpt_Edit", { type: "error" });
                            exceptSetErrorEdit("exceptLtrStyle_Edit", { type: "error" });
                            toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Duplicate entry detected.', life: 3000 });
                            return;
                        }

                        saveExcept(_except);
                        resetExcept();
                        resetExceptEdit();
                        setIsRowEditingExcept(false);
                        rowEditor.onSaveClick(e);
                    }} />

                    <Button icon="p-row-editor-cancel-icon pi pi-fw pi-undo" title="Undo" rounded text severity="danger" onClick={(e) => {
                        resetExceptEdit();
                        except.exceptSetupOptId = rowData.exceptSetupOptId;
                        except.exceptColorOpt = rowData.exceptColorOpt;
                        except.exceptSzOpt = rowData.exceptSzOpt;
                        except.exceptLtrItmClass = rowData.exceptLtrItmClass;
                        except.exceptLtrStyle = rowData.exceptLtrStyle;
                        setIsRowEditingExcept(false);
                        rowEditor.onCancelClick(e);
                    }} />
                </>
            );
        } else {
            return (
                <Link className="pi pi-fw pi-trash rowDelete" title="Delete" onClick={() => confirmDeleteExcept(rowData)} to={''}></Link>
            );
        }
    }

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilter(value);
    };

    const headerExceptsTable = (
        <div className="flex flex-column md:flex-row md:justify-content-end md:align-items-center">
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText className="p-inputtext-sm" type="search" autoComplete="off" disabled={isRowEditingExcept} value={globalFilter} onChange={onGlobalFilterChange} placeholder="Search..." />
            </IconField>
        </div>
    );

    const deleteExceptDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteExceptDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteExcept} />
        </>
    );

    const selectedStyleTemplate = (option: Style, props: any) => {
        if (option) {
            return (option.styleCode + ' - ' + option.description);
        }

        return <span>{props.placeholder}</span>;
    };

    const styleTemplate = (option: Style) => {
        return (option.styleCode + ' - ' + option.description);
    };

    const [loading, setLoading] = useState(false);

    const loadDropdownData = async (query: string) => {
        setLoading(true);
        try {
            const data = await ExceptService.getLetteringStyles(query);
            setLetteringStyles(data);
        } catch (error) {
            console.error("Error fetching items:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDropdownFilter = (e: any) => {
        const searchText = e.filter;
        loadDropdownData(searchText);
    };

    return (
        <div className="grid-nogutter">
            <div className="col-12">
                <Toast ref={toast} />

                <Panel header="Add Detailing Exception" className="addPanel mb-2 -ml-2">
                    <form key={2} onSubmit={handleSubmitExcept(onSubmitExcept)} className="p-fluid" autoComplete="off">
                        <div className="formgrid grid">
                            <div className="field col-2">
                                <Controller
                                    name="exceptSetupOptId"
                                    control={controlExcept}
                                    defaultValue={null}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Detailing Option</label>
                                            <Dropdown value={field.value} options={setupOpt} optionLabel="setupOptDesc" optionValue="setupOptId" showClear className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => {
                                                field.onChange(e.value);
                                                if (!styleCode || !setupOpt) { return; }
                                                var setupOptDesc = setupOpt.find(option => option.setupOptId === e.value)?.setupOptDesc;
                                                if (!setupOptDesc) { return; }
                                                ExceptService.getSetupOptByStyleCdAndDesc(styleCode, setupOptDesc).then(data => {
                                                    setSetupOptColor(data);
                                                });

                                            }} placeholder="Detailing Option" />
                                            {getExceptFormErrorMessage('exceptSetupOptId')}
                                        </>
                                    )}
                                />
                            </div>
                            <div className="field col-2">
                                <Controller
                                    name="exceptColorOpt"
                                    control={controlExcept}
                                    defaultValue={null}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Color Exception</label>
                                            <Dropdown value={field.value} options={setupOptColor} optionLabel="setupOptDesc" optionValue="setupOptId" showClear className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => { field.onChange(e.value); }} placeholder="Color Exception" />
                                            {getExceptFormErrorMessage('exceptColorOpt')}
                                        </>
                                    )}
                                />
                            </div>
                            <div className="field col-2">
                                <Controller
                                    name="exceptSzOpt"
                                    control={controlExcept}
                                    defaultValue={null}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Size Exception</label>
                                            <Dropdown value={field.value} options={setupOptSize} optionLabel="setupOptDesc" optionValue="setupOptId" showClear className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => { field.onChange(e.value); }} placeholder="Size Exception" />
                                            {getExceptFormErrorMessage('exceptSzOpt')}
                                        </>
                                    )}
                                />
                            </div>
                            <div className="field col-2">
                                <Controller
                                    name="exceptLtrStyle"
                                    control={controlExcept}
                                    defaultValue={null}
                                    render={({ field, fieldState }) => (
                                        <>
                                            <label htmlFor={field.name} className="font-bold text-xs">Lettering Style</label>
                                            <Dropdown
                                                value={field.value}
                                                options={letteringStyles}
                                                onChange={(e) => field.onChange(e.value)}
                                                filter
                                                filterBy="styleCode"
                                                onFilter={handleDropdownFilter}
                                                itemTemplate={styleTemplate}
                                                valueTemplate={selectedStyleTemplate}
                                                optionLabel="description"
                                                optionValue="styleCode"
                                                showClear
                                                className={classNames({ 'p-invalid border-left-10': fieldState.error })}
                                                placeholder="Lettering Style"
                                                loading={loading}
                                            />
                                            {getExceptFormErrorMessage('exceptLtrStyle')}
                                        </>
                                    )}
                                />
                            </div>
                            <div className="field field-buttons col-2 pt-4 ml-3">
                                <Button icon="pi pi-save" title="Save" rounded text severity="info" type="submit" />
                                <Button icon="pi pi-undo" title="Undo" rounded text severity="danger" type="button" onClick={resetExcept} />
                            </div>
                        </div>
                    </form>
                </Panel>

                <DataTable value={excepts} className="-ml-2" selectionMode="single" selection={except} onRowClick={onRowClickExcept}
                    dataKey="exceptId" size="small" rowHover={true} loading={loadingExcepts}
                    scrollable scrollHeight="calc(100vh - 34.5rem)"
                    paginator rows={25} rowsPerPageOptions={[5, 10, 25]} alwaysShowPaginator={false}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                    editingRows={editingRows} editMode="row" onRowEditChange={onRowEditChange}
                    filters={filters} emptyMessage="No exceptions records found." header={headerExceptsTable}>
                    <Column field="exceptId" header="Exception Id" sortable body={exceptId_Template} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                    <Column field="exceptSetupOptId" header="Detailing Option" sortable body={exceptSetupOptId_Template} editor={(options: ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                    <Column field="exceptColorOpt" header="Color Exception" sortable body={exceptColorOpt_Template} editor={(options: ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                    <Column field="exceptSzOpt" header="Size Exception" sortable body={exceptSzOpt_Template} editor={(options: ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                    <Column field="exceptLtrStyle" header="Lettering Style" sortable body={exceptLtrStyle_Template} editor={(options: ColumnEditorOptions) => cellEditor(options)} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                    <Column field="exceptLtrItmClass" header="Lettering Class" sortable body={exceptLtrItmClass_Template} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                    <Column field="exceptUpdTs" header="Last Update Date" sortable body={exceptUpdTs_Template} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                    <Column field="exceptUpdUser" header="Last Update User" sortable body={exceptUpdUser_Template} align="center" alignHeader="center" headerStyle={{ width: '10%', minWidth: '10rem' }}></Column>
                    <Column rowEditor body={rowEditorExceptTemplate} bodyStyle={{ textAlign: 'center' }}></Column>
                </DataTable>

                <Dialog visible={deleteExceptDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }} header="Confirm" modal footer={deleteExceptDialogFooter} onHide={hideDeleteExceptDialog}>
                    <div className="confirmation-content">
                        <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                        {except && <span>Are you sure you want to delete exception <b>{except.exceptId}</b>?</span>}
                    </div>
                </Dialog>
            </div>
        </div>
    );
};

export default StyleExcept;
